import { useCallback, useEffect, useRef, useState } from "react";
import { workingSessionSchema, type SyncTransportError, type WorkingSession } from "@bedrock/domain";
import { SyncRequestError } from "@bedrock/local-store";

export type StartupMode = "library" | "continue";
export interface WorkingSessionCloud {
  get(): Promise<WorkingSession | null>;
  save(session: WorkingSession): Promise<WorkingSession>;
}

export function loadStartupMode(): StartupMode {
  try { return localStorage.getItem("bedrock-startup") === "continue" ? "continue" : "library"; }
  catch { return "library"; }
}
export function saveStartupMode(mode: StartupMode): void {
  try { localStorage.setItem("bedrock-startup", mode); } catch { /* preferences remain usable in memory */ }
}
export function readWorkingSession(scope: string): WorkingSession | null {
  try {
    const parsed = workingSessionSchema.safeParse(JSON.parse(localStorage.getItem(`bedrock-working-session:${scope}`) ?? "null"));
    return parsed.success ? parsed.data : null;
  } catch { return null; }
}
export function newerWorkingSession(local: WorkingSession | null, remote: WorkingSession | null): WorkingSession | null {
  return !local || (remote && remote.updatedAt > local.updatedAt) ? remote : local;
}

export function useWorkingSession(scope: string, cloud?: WorkingSessionCloud) {
  const [session, setSession] = useState(() => readWorkingSession(scope));
  const latest = useRef(session);
  const [ready, setReady] = useState(!cloud);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<SyncTransportError | null>(null);
  const retryTransfer = useRef<(() => void) | null>(null);
  const retry = useCallback(() => retryTransfer.current?.(), []);
  const published = useRef(0);
  const update = useCallback((next: WorkingSession) => {
    latest.current = next;
    try { localStorage.setItem(`bedrock-working-session:${scope}`, JSON.stringify(next)); } catch { /* keep the in-memory session */ }
    setSession(next);
  }, [scope]);

  useEffect(() => {
    if (!cloud) return;
    let active = true;
    let loading = false;
    let saving = false;
    let loaded = false;
    let retryAfter = 0;
    let blocked = false;
    let needsSignIn = false;
    const transfer = async (force = false) => {
      if (!active || loading || saving || (!force && (blocked || Date.now() < retryAfter))) return;
      if (force) { blocked = false; retryAfter = 0; }
      try {
        if (!loaded) {
          loading = true;
          const remote = await cloud.get();
          if (!active) return;
          loaded = true;
          published.current = remote?.updatedAt ?? 0;
          const next = newerWorkingSession(latest.current, remote);
          if (next) update(next);
          setReady(true);
        }
        const next = latest.current;
        if (!next || next.updatedAt <= published.current) { setPending(false); setError(null); return; }
        saving = true;
        setPending(true);
        const saved = await cloud.save(next);
        if (!active) return;
        published.current = saved.updatedAt;
        if (latest.current === next && saved.updatedAt > next.updatedAt) update(saved);
        setPending((latest.current?.updatedAt ?? 0) > published.current);
        setError(null);
      } catch (cause) {
        const detail: SyncTransportError = cause instanceof SyncRequestError ? cause.detail : { code: "network_error", message: "The cloud is temporarily unreachable", status: null, retryable: true, retryAt: null, correlationId: null };
        blocked = !detail.retryable;
        needsSignIn = detail.code === "sign_in_required" || detail.code === "unauthorized";
        retryAfter = Math.max(Date.now() + 15_000, detail.retryAt ?? 0);
        if (active) { setReady(true); setError(detail); setPending(Boolean(latest.current && latest.current.updatedAt > published.current)); }
      } finally { loading = false; saving = false; }
    };
    void transfer();
    const timer = setInterval(() => void transfer(), 1500);
    retryTransfer.current = () => void transfer(true);
    const retryWhenConnected = () => void transfer(needsSignIn);
    window.addEventListener("online", retryWhenConnected);
    window.addEventListener("focus", retryWhenConnected);
    return () => { active = false; retryTransfer.current = null; clearInterval(timer); window.removeEventListener("online", retryWhenConnected); window.removeEventListener("focus", retryWhenConnected); };
  }, [cloud, update]);

  const remember = useCallback((patch: Pick<WorkingSession, "spaceId" | "itemId"> & Partial<Pick<WorkingSession, "cursor" | "scrollTop">>, deviceId: string) => {
    const previous = latest.current;
    const sameItem = previous?.spaceId === patch.spaceId && previous.itemId === patch.itemId;
    const next = { ...patch, cursor: patch.cursor ?? (sameItem ? previous.cursor : 0), scrollTop: patch.scrollTop ?? (sameItem ? previous.scrollTop : 0), deviceId, updatedAt: Math.max(Date.now(), (previous?.updatedAt ?? 0) + 1) };
    update(next);
    if (cloud) setPending(true);
  }, [cloud, update]);
  return { session, ready, pending, error, retry, remember };
}
